/**
 * Audio catalog — the two channels of workshop audio (specs/audio-management.md):
 *
 *   SFX          one-shot named tones, fire-and-forget (phase chime, ding…)
 *   BACKGROUNDS  looping ambient tracks per tila, faded in/out
 *
 * Files live in public/audio/. The current .wav files are synthesized
 * placeholders — drop the real kuutio-sfx clips over them (same names) or
 * add entries here.
 */
export const SFX = {
  chime: { label: 'Kello — vaihe vaihtuu', file: '/audio/sfx/chime.wav' },
  ding: { label: 'Ding — vastaus saapui', file: '/audio/sfx/ding.wav' },
  tick: { label: 'Tikitys — lähtölaskenta', file: '/audio/sfx/tick.wav' },
  buzz: { label: 'Summeri — virhe', file: '/audio/sfx/buzz.wav' },
};

export const BACKGROUNDS = {
  'ambient-calm': { label: 'Rauhallinen tausta', file: '/audio/background/ambient-calm.wav' },
  'ambient-energy': { label: 'Energinen tausta', file: '/audio/background/ambient-energy.wav' },
};

/** Which screen plays audio when the tila doesn't say (venue speakers hang off one machine). */
export const AUDIO_HOST_DEFAULT = 'A1';

/**
 * SCAPES — spatial ambient soundscapes: every wall screen plays its OWN stem
 * through its own speakers. Uncorrelated sources need no clock sync — eight
 * independent stems localize like a real environment. Stem loop lengths are
 * deliberately different so the room-wide combination never repeats.
 *
 * Placeholder stems are synthesized (mp3, ~100 KB each) — replace with
 * curated recordings per scape when available. Note: mp3 loops have a tiny
 * encoder gap at the seam; fine for beds, use ogg for gapless when curating.
 */
export const SCAPES = {
  forest: {
    label: 'Metsä',
    stems: [
      '/audio/scapes/forest/wind-soft.mp3',
      '/audio/scapes/forest/wind-trees.mp3',
      '/audio/scapes/forest/wind-gusts.mp3',
      '/audio/scapes/forest/leaves.mp3',
      '/audio/scapes/forest/birds-close.mp3',
      '/audio/scapes/forest/birds-mid.mp3',
      '/audio/scapes/forest/birds-far.mp3',
      '/audio/scapes/forest/cuckoo.mp3',
    ],
  },
  coast: {
    label: 'Rannikko',
    stems: [
      '/audio/scapes/coast/waves-slow.mp3',
      '/audio/scapes/coast/waves-mid.mp3',
      '/audio/scapes/coast/waves-wash.mp3',
      '/audio/scapes/coast/sea-wind.mp3',
      '/audio/scapes/coast/sea-breeze.mp3',
      '/audio/scapes/coast/waves-far.mp3',
      '/audio/scapes/coast/gull-far.mp3',
      '/audio/scapes/coast/shore-air.mp3',
    ],
  },
};

const WALL_SCREENS = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'D1', 'D2'];

function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h;
}

/**
 * Deterministic stem for this screen: every client computes the same seeded
 * shuffle of the scape's stems (Fisher–Yates on a small LCG), so the whole
 * room agrees on the premade layout without any per-screen messaging —
 * same philosophy as the cube physics. Same seed → same layout.
 */
export function stemForScreen(scapeKey, seed, screenCode) {
  const scape = SCAPES[scapeKey];
  if (!scape) return null;
  let state = hashString(`${scapeKey}:${seed || 'default'}`) || 1;
  const rand = () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 0xffffffff;
  };
  const order = [...scape.stems];
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  const idx = WALL_SCREENS.indexOf(screenCode);
  if (idx === -1) return order[0];
  return order[idx % order.length];
}
